import { logger } from "../utils/logger";
import { buildCoreDemo } from "./builders/core_builder";
import { buildLegacyDemoResult } from "./compatibility/demo_compatibility";
import { buildDemoConfig } from "./config/demo_defaults";
import { getDemoIntent } from "./config/demo_options";
import { getIndustryTemplate } from "./industries";
import { getSeedProfile } from "./profiles";
import { createInitialDemoReport } from "./report/demo_report";
import {
  cleanupFailedExperience,
  completeExperienceRun,
  createExperienceRun,
  failExperienceRun,
  updateExperienceRun,
} from "./run_tracking/experience_run_tracker";

export const createDemo = async ({
  user,
  demoConfig: incomingDemoConfig = {},
  onProgress,
} = {}) => {
  let runId = null;
  let eventId = null;

  try {
    const intent = getDemoIntent(incomingDemoConfig.intent);
    const initialConfig = buildDemoConfig({
      ...incomingDemoConfig,
      expiration: {
        ...(incomingDemoConfig.expiration || {}),
        ...intent.expiration,
      },
    });

    const industryTemplate = getIndustryTemplate(initialConfig.industry);

    if (!industryTemplate) {
      throw new Error(
        `Experience industry "${initialConfig.industry}" is not registered.`
      );
    }

    runId = await createExperienceRun({ user, demoConfig: initialConfig });

    const demoConfig = { ...initialConfig, runId };
    const seedProfile = getSeedProfile(demoConfig.profile);
    const report = createInitialDemoReport({
      demoConfig: { ...demoConfig, seedProfile },
      industryTemplate,
    });

    const result = await buildCoreDemo({
      user,
      demoConfig,
      seedProfile,
      industryTemplate,
      report,
      runId,
      onProgress: async (progress) => {
        eventId = progress?.eventId || eventId;

        try {
          await updateExperienceRun(runId, {
            status: progress?.phase === "failed" ? "failed" : "running",
            eventId: eventId || null,
            currentStep: progress?.step || null,
            currentStepLabel: progress?.label || null,
            completedSteps:
              progress?.phase === "completed"
                ? progress.current
                : Math.max(0, (progress?.current || 1) - 1),
            totalSteps: progress?.total || 0,
          });
        } catch (trackingError) {
          logger.warn("Experience run progress could not be persisted", {
            value: trackingError,
          });
        }

        onProgress?.(progress);
      },
    });

    eventId = result?.eventId || eventId;

    try {
      await completeExperienceRun({
        runId,
        eventId,
        report: result?.demoEngine?.report,
      });
    } catch (trackingError) {
      logger.warn("Completed Experience run could not be finalized", {
        value: trackingError,
      });
    }

    logger.log("Experience Engine completed", { value: result });

    return buildLegacyDemoResult({ demoEngineResult: result });
  } catch (error) {
    error.experienceEventId = eventId;
    error.experienceRunId = runId;
    await failExperienceRun({ runId, eventId, error }).catch(() => {});

    if (eventId) {
      try {
        await cleanupFailedExperience({ runId, eventId });
        error.experienceCleanup = "cleaned";
      } catch (cleanupError) {
        error.experienceCleanup = "cleanup_required";
        logger.error("Failed to clean up partial Experience", {
          value: cleanupError,
        });
      }
    }

    logger.error("Experience Engine failed", { value: error });
    throw error;
  }
};
